import React, { ReactNode } from "react";
import { Layout } from "antd";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contextProviders/AuthProvider";
import AppHeader from "./AppHeader";
import AppFooter from "./AppFooter";
import MobileBottomNav from "./MobileBottomNav";
import { useAppBreakpoint } from "../../hooks/useAppBreakpoint";
import { TRANSLATIONS } from "../../constants/translations";
import { translate } from "../../helpers/translationHelper";

const { Content } = Layout;

// Bottom nav's own height (56px tab row) plus a little breathing room, so
// page content never scrolls up underneath it -- MobileBottomNav no longer
// has an elevated element that would need extra clearance (its old central
// "+" moved into AppHeader instead). env(safe-area-inset-bottom) is the
// bar's own padding, added again here since that padding grows the bar's
// total height on notched devices too.
const MOBILE_BOTTOM_NAV_CLEARANCE = 'calc(60px + 12px + env(safe-area-inset-bottom, 0px))';

interface AppLayoutProps {
  children: ReactNode;
  // Pages that lock their own content to exactly the viewport height below
  // AppHeader (see Messages.tsx) can't also carry the site-wide footer --
  // Layout/Content below only ever set `minHeight`, never a hard cap, so
  // the footer's own height simply gets added past that viewport-height
  // box, pushing the whole document taller than the viewport and
  // producing a page-level scrollbar that fights the page's own internal
  // one. Rendering it conditionally (not just visually hiding it) is what
  // actually keeps the document from growing, since a hidden-but-present
  // footer would still reserve its box height.
  hideFooter?: boolean;
  // Mobile only: hides MobileBottomNav entirely instead of just letting it
  // auto-hide on scroll (see that component's own `hidden` prop) -- for a
  // screen with its own keyboard-pinned bottom input (Messages' open
  // thread) that the bar would otherwise sit on top of. The clearance
  // padding below is dropped along with it, since there's no bar left to
  // leave room for.
  hideBottomNav?: boolean;
  // Mobile only, opt-in: skips the generic AppHeader entirely for a screen
  // that renders its own dedicated fixed header instead (e.g. /explore's
  // results screen -- back arrow + search + filters, not "+"/notifications).
  // Content's own marginTop/minHeight (which otherwise assume AppHeader's
  // 64px) drop to 0/100dvh along with it.
  hideHeader?: boolean;
}

/** Every authenticated page renders through this — same header/footer everywhere (unless hideFooter opts out), never reimplemented page by page. */
const AppLayout: React.FC<AppLayoutProps> = ({ children, hideFooter, hideBottomNav, hideHeader }) => {
  const { loading, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();

  if (loading) return <div>{translate(t, TRANSLATIONS.LAYOUT_LOADING)}</div>;
  if (!isAuthenticated) return null;

  return (
    <Layout style={{ minHeight: "100dvh" }}>
      {!hideHeader && <AppHeader />}
      <Content
        style={{
          padding: "24px",
          marginTop: hideHeader ? 0 : 64,
          display: "flex",
          justifyContent: "center",
          minHeight: hideHeader ? "100dvh" : "calc(100dvh - 64px)",
          // Bottom nav replaces the footer as the mobile page's own bottom
          // chrome (see AppFooter below) -- this is what keeps page content
          // from ending up hidden behind the fixed bar instead.
          paddingBottom: isMobile && !hideBottomNav ? MOBILE_BOTTOM_NAV_CLEARANCE : undefined,
        }}
      >
        {children}
      </Content>
      {/* AppFooter's link-heavy site footer has nowhere good to go above a
          fixed bottom tab bar on a real phone screen, so mobile drops it
          entirely instead of stacking both -- desktop/tablet are untouched. */}
      {!hideFooter && !isMobile && <AppFooter />}
      <MobileBottomNav hidden={hideBottomNav} />
    </Layout>
  );
};

export default AppLayout;
